import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  console.log('🌐 API Request interceptor:', config.method.toUpperCase(), config.url);
  const token = localStorage.getItem('authToken');
  console.log('🔑 Token from localStorage:', token ? token.substring(0, 20) + '...' : 'NONE');

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('✅ Authorization header added');
  } else {
    console.log('❌ No token found, request will be sent without auth');
  }

  return config;
});

// Auth API
export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  loginWithGoogle: (googleIdToken) => api.post('/auth/google', { googleIdToken }),
  loginWithApple: (appleIdToken) => api.post('/auth/apple', { appleIdToken }),
  webSignupOrSignin: (data) => api.post('/auth/web/signup-or-signin', data),
  syncCode: (syncCode) => api.post('/auth/sync', { syncCode }),
  createGuest: (data) => api.post('/auth/guest/create', data),
};

// User API
export const user = {
  getProfile: () => api.get('/user/profile'),
  generateSyncCode: () => api.post('/user/sync-code'),
  getVideoMeetingRoom: (languageId) => api.get(`/user/video-meeting-room?languageId=${languageId}`),
  subscribe: () => api.post('/user/subscribe'),
  linkGoogle: (googleIdToken) => api.post('/user/link-google', { googleIdToken }),
};

// Languages API
export const languages = {
  getAll: () => api.get('/user/languages'),
  get: (id) => api.get(`/user/languages/${id}`),
  create: (data) => api.post('/user/languages', data),
  update: (id, data) => api.put(`/user/languages/${id}`, data),
  delete: (id) => api.delete(`/user/languages/${id}`),
  getSupported: () => api.get('/languages/supported'),
};

// Conversations API
export const conversations = {
  getAll: () => api.get('/conversations'),
  create: (data) => api.post('/conversations', data),
  sendMessage: (id, message) => api.post(`/conversations/${id}/message`, message),
};

// Flashcards API
export const flashcards = {
  getAll: () => api.get('/flashcards'),
  create: (data) => api.post('/flashcards', data),
  update: (id, data) => api.put(`/flashcards/${id}`, data),
  delete: (id) => api.delete(`/flashcards/${id}`),
};

// Meetings API
export const meetings = {
  getAll: () => api.get('/meetings'),
  get: (id) => api.get(`/meetings/${id}`),
  create: (data) => api.post('/meetings', data),
  updateStatus: (id, status) => api.patch(`/meetings/${id}/status`, { status }),
  toggleProcessing: (id, enabled, reason = 'button') =>
    api.post(`/meetings/${id}/toggle-processing`, { enabled, reason }),
  delete: (id) => api.delete(`/meetings/${id}`),
  verify: (roomId) => api.get(`/meetings/verify/${roomId}`),
};

// Transcriptions API
export const transcriptions = {
  getAll: (params) => api.get('/user/transcriptions', { params }),
  get: (id) => api.get(`/user/transcriptions/${id}`),
  getAnalysis: (id) => api.get(`/transcriptions/${id}/analysis`),
  delete: (id) => api.delete(`/user/transcriptions/${id}`),
};

// Sessions API (for tracking meeting sessions)
export const sessions = {
  startSession: (data) => api.post('/user/sessions/start', data),
};

// Subscription API
export const subscription = {
  createCheckoutSession: (data) => {
    // In local development, use mock endpoint (no real Stripe needed)
    // Check if we're in local dev by looking at API base URL
    const isLocalDev = API_BASE.includes('localhost') || API_BASE.includes('127.0.0.1');

    if (isLocalDev) {
      console.log('🔧 Using dev mock subscription endpoint (no Stripe needed)');
      return api.post('/dev/mock-subscribe', data);
    }

    // In production, use real Stripe
    return api.post('/subscription/create-checkout-session', data);
  },
};

// Teacher API
export const teachers = {
  search: (languageId) => {
    const params = languageId ? `?languageId=${languageId}` : '';
    return api.get(`/teachers/search${params}`);
  },
  getProfile: (teacherId) => api.get(`/teacher/profile/${teacherId}`),
};

// Messages API (Direct messaging between students and teachers)
export const messages = {
  send: (data) => api.post('/messages', data),
  getConversations: () => api.get('/messages/conversations'),
  getConversation: (userId) => api.get(`/messages/conversation/${userId}`),
  getUnreadCount: () => api.get('/messages/unread-count'),
};

// Admin API (requires JWT auth + admin role)
export const admin = {
  getStats: () => api.get('/admin/stats'),
  getTopConsumersTokens: (limit = 10) => api.get(`/admin/top-consumers/tokens?limit=${limit}`),
  getTopConsumersTime: (limit = 10) => api.get(`/admin/top-consumers/time?limit=${limit}`),
  getRecentActivity: (limit = 50) => api.get(`/admin/recent-activity?limit=${limit}`),
  searchUser: (email) => api.get(`/admin/users/search?email=${encodeURIComponent(email)}`),
  grantLifetimeFree: (userId, reason) => api.post('/admin/users/grant-lifetime-free', { userId, reason }),
  grantFreeMonths: (userId, months, reason) => api.post('/admin/users/grant-free-months', { userId, months, reason }),
  toggleRole: (userId, role) => api.post('/admin/users/toggle-role', { userId, role }),
  deleteUser: (userId, reason) => api.post('/admin/users/delete', { userId, reason }),
};

export default api;
